import { useRemoteOperationsAreaOperatorRole } from '@backoffice-frontend/ad-remote-operations-area'
import {
  useErrorHandlingMutation,
  useErrorHandlingPollingQuery,
} from '@backoffice-frontend/data-fetching'
import { FccOperatorAvailabilityType } from '@backoffice-frontend/graphql'
import { useUserHasAccess } from '@backoffice-frontend/restricted'
import { useGlobalRegion } from '@backoffice-frontend/service-area-picker'
import { BoumRight } from '@moia-dev/moia-token-claims'
import { createContext, useContext, useEffect, useEffectEvent } from 'react'
import { roleLoginRight } from './roleLoginRight'
import {
  AdOperatorAvailabilityDocument,
  useAdOperatorAvailabilityQuery,
  useSetAdOperatorAvailabilityMutation,
} from './useAdOperatorAvailability.hook'
import { useMicrophonePermission } from './useMicrophonePermission'

const OPERATOR_AVAILABILITY_POLL_INTERVALL = 5000

function useAdOperatorAvailabilityContextValue() {
  const role = useRemoteOperationsAreaOperatorRole()

  const isAllowed = useUserHasAccess({
    requiredRights: [
      BoumRight.AD_FLEET_CONTROL_OPERATOR_CONNECT,
      ...(role ? [roleLoginRight[role]] : []),
    ],
  })
  const [region] = useGlobalRegion()
  const { isMicrophoneEnabled, enableMicrophone } = useMicrophonePermission()

  const { data, loading } = useErrorHandlingPollingQuery(
    useAdOperatorAvailabilityQuery,
    region && isAllowed
      ? {
          variables: { regionId: region.id, role },
          pollInterval: OPERATOR_AVAILABILITY_POLL_INTERVALL,
          fetchPolicy: 'network-only',
        }
      : { skip: true },
  )

  const [setAvailability, { loading: isToggling }] = useErrorHandlingMutation(
    useSetAdOperatorAvailabilityMutation,
    {
      refetchQueries: [AdOperatorAvailabilityDocument],
    },
  )

  const availability =
    data?.fccOperatorAvailability?.availability ??
    FccOperatorAvailabilityType.Offline

  const isOnline = availability !== FccOperatorAvailabilityType.Offline
  const isBusy = availability === FccOperatorAvailabilityType.Busy

  const toggleAvailability = () => {
    if (isToggling || isBusy || !region?.id) return

    const newAvailability = isOnline
      ? FccOperatorAvailabilityType.Offline
      : FccOperatorAvailabilityType.Available

    setAvailability({
      variables: {
        availability: newAvailability,
        regionId: region.id,
        role,
      },
    })
  }

  const checkMicrophonePermissionEvent = useEffectEvent(enableMicrophone)

  useEffect(() => {
    if (availability !== FccOperatorAvailabilityType.Offline) {
      checkMicrophonePermissionEvent()
    }
  }, [availability])

  return {
    availability,
    loading,
    isActive:
      availability === FccOperatorAvailabilityType.Available ||
      availability === FccOperatorAvailabilityType.Busy,
    isOffline: availability === FccOperatorAvailabilityType.Offline,
    isAvailable: availability === FccOperatorAvailabilityType.Available,
    isOnline,
    isBusy,
    isToggling,
    toggleAvailability,

    isMicrophoneEnabled,
    enableMicrophone,
  }
}

type AdOperatorAvailabilityContextValue = ReturnType<
  typeof useAdOperatorAvailabilityContextValue
>

const AdOperatorAvailabilityContext =
  createContext<AdOperatorAvailabilityContextValue | null>(null)

export function AdOperatorAvailabilityProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const adOperatorAvailabilityContextValue =
    useAdOperatorAvailabilityContextValue()

  return (
    <AdOperatorAvailabilityContext.Provider
      value={adOperatorAvailabilityContextValue}
    >
      {children}
    </AdOperatorAvailabilityContext.Provider>
  )
}

export const useAdOperatorAvailability = () => {
  const operatorAvailabilityContextValue = useContext(
    AdOperatorAvailabilityContext,
  )

  if (operatorAvailabilityContextValue === null) {
    throw new Error(
      'useOperatorAvailability must be used within OperatorAvailabilityProvider',
    )
  }

  return operatorAvailabilityContextValue
}
